// The twisted-silk ribbon from the rarion.uk hero, without React (#3192).
// A flat plane strip is deformed in the vertex shader onto a cubic Bézier
// spline with twist; the fragment shader paints a colour run along the band,
// a drifting fold highlight and a fresnel rim. It mounts into the empty
// [data-ribbon] hosts that marketing/v2/shared/_ribbon renders, as a
// progressive enhancement: copy stays server-rendered, reduced motion gets a
// still frame of the same band, and without WebGL or JavaScript the host
// stays empty (a flat SVG imitation read as a different ribbon, so there is
// no static drawing). The hero host fills its hero at every width; on phones
// the closing host is a strip in the page flow that only takes its height
// once boot has confirmed WebGL (data-ribbon-ready), so a phone without it
// keeps its copy where it was; and the loop survives
// the context loss iOS and Android hand out under memory pressure.
import type * as THREE from "three";

type Three = typeof THREE;
export type Placement = "hero" | "closing";
export type Vec3 = [number, number, number];
export type ControlPoints = [Vec3, Vec3, Vec3, Vec3];

export const RIBBON_READY_EVENT = "rarion:ribbon-ready";

export interface RibbonHandle {
  readonly placement: Placement;
  // The choreography (#3201) feeds the band's scroll progress from its own
  // trigger; null hands measurement back to the frame loop.
  setScrollProgress(progress: number | null): void;
  setAnimated(on: boolean): void;
  dispose(): void;
}

const VERTEX_SHADER = `
uniform float u_time;
uniform float u_scroll;
uniform vec3 u_cp0;
uniform vec3 u_cp1;
uniform vec3 u_cp2;
uniform vec3 u_cp3;

varying vec2 vUv;
varying vec3 vNormalW;
varying vec3 vViewDir;

vec3 cubicBezier(float t, vec3 p0, vec3 p1, vec3 p2, vec3 p3) {
  float u = 1.0 - t;
  return u * u * u * p0
       + 3.0 * u * u * t * p1
       + 3.0 * u * t * t * p2
       + t * t * t * p3;
}

vec3 cubicBezierTangent(float t, vec3 p0, vec3 p1, vec3 p2, vec3 p3) {
  float u = 1.0 - t;
  return 3.0 * u * u * (p1 - p0)
       + 6.0 * u * t * (p2 - p1)
       + 3.0 * t * t * (p3 - p2);
}

void main() {
  float along = uv.x;
  float across = uv.y - 0.5;

  // Slow wobble on the inner control points: a gentle living undulation.
  float wob = u_time * 0.18;
  vec3 cp1 = u_cp1 + vec3(sin(wob * 0.9) * 0.05, cos(wob * 0.7) * 0.05, sin(wob * 1.3) * 0.10);
  vec3 cp2 = u_cp2 + vec3(cos(wob * 0.8) * 0.05, sin(wob * 1.1) * 0.05, cos(wob * 0.6) * 0.10);

  vec3 p = cubicBezier(along, u_cp0, cp1, cp2, u_cp3);
  vec3 tangent = normalize(cubicBezierTangent(along, u_cp0, cp1, cp2, u_cp3));

  vec3 up = vec3(0.0, 0.0, 1.0);
  vec3 binormal = normalize(cross(tangent, up));
  vec3 normal = normalize(cross(binormal, tangent));

  // ~0.9 turns over the visible length plus a slow temporal oscillation.
  // Scrolling the hero away turns the band a little further (v4 motion).
  float twistPhase = 0.35 * sin(u_time * 0.18) + u_scroll * 1.2;
  float tw = (along - 0.5) * 5.65 + twistPhase;
  vec3 b = binormal * cos(tw) + normal * sin(tw);
  vec3 n = -binormal * sin(tw) + normal * cos(tw);

  float widthProfile = 1.0 + 0.25 * sin(along * 3.14159);
  float halfWidth = 0.28 * widthProfile;

  // Parabolic cross-section thickness so edge-on angles read as an arc.
  float thicknessProfile = 1.0 - 4.0 * across * across;
  float thickness = 0.065;
  vec3 thicknessOffset = n * thickness * thicknessProfile;

  vec3 offset = b * halfWidth * (across * 2.0);
  vec3 finalPos = p + offset + thicknessOffset;
  // The band lags the page as it scrolls: a touch of parallax.
  finalPos.y -= u_scroll * 0.35;

  vec3 surfaceNormal = normalize(b * (8.0 * thickness * across) + n * (2.0 * halfWidth));

  vUv = uv;
  vNormalW = normalize(normalMatrix * surfaceNormal);
  vec4 mvPosition = modelViewMatrix * vec4(finalPos, 1.0);
  vViewDir = normalize(-mvPosition.xyz);
  gl_Position = projectionMatrix * mvPosition;
}
`;

// How far the self-inverted band (paintsInverted) lifts its inverse above a
// pure inversion: brighter copy over the band, deeper band under the copy.
export const INVERTED_LIFT = 0.15;

// No precision statement of its own: three.js prepends the highest the GPU
// supports, so a phone whose fragment shaders lack highp still compiles it.
const FRAGMENT_SHADER = `
uniform float u_time;
uniform float u_reverse;
uniform float u_invert;
uniform vec3 u_color0;
uniform vec3 u_color1;
uniform vec3 u_color2;
uniform vec3 u_color3;
uniform vec3 u_color4;

varying vec2 vUv;
varying vec3 vNormalW;
varying vec3 vViewDir;

void main() {
  // The closing band runs the colours the other way, green entering and blue leaving, as in the mock.
  float along = mix(vUv.x, 1.0 - vUv.x, u_reverse);
  float across = vUv.y;
  float acrossCentered = across - 0.5;

  vec3 color;
  if (along < 0.22) {
    color = mix(u_color4, u_color0, smoothstep(0.00, 0.22, along));
  } else if (along < 0.45) {
    color = mix(u_color0, u_color1, smoothstep(0.22, 0.45, along));
  } else if (along < 0.70) {
    color = mix(u_color1, u_color2, smoothstep(0.45, 0.70, along));
  } else {
    color = mix(u_color2, u_color3, smoothstep(0.70, 1.00, along));
  }

  float edgeFalloff = 1.0 - pow(abs(acrossCentered) * 2.0, 2.0);
  color *= 0.88 + 0.20 * edgeFalloff;

  float foldPhase = 0.5 + 0.5 * sin(along * 5.0 + u_time * 0.40);
  float fold = smoothstep(0.12, 0.0, abs(across - foldPhase));
  color += vec3(1.0) * fold * 0.10;

  float fres = pow(1.0 - abs(dot(normalize(vNormalW), normalize(vViewDir))), 2.0);
  color *= 0.88 + 0.25 * fres;

  float alphaAlong = smoothstep(-0.02, 0.08, along) * smoothstep(1.02, 0.92, along);
  float alphaAcross = smoothstep(0.00, 0.06, across) * smoothstep(1.00, 0.94, across);

  // A host the stylesheet blends in difference (see paintsInverted) paints
  // the band inverted, so the blend gives the page the band back and the
  // copy under it the inversion the desktop blend makes. The inverse is
  // lifted by INVERTED_LIFT: the copy over the band comes out that much
  // brighter than a pure inversion could make it, and the band under the
  // copy that much deeper, for the contrast the v9 feedback asked for.
  vec3 inverse = 1.0 - clamp(color, 0.0, 1.0) * (1.0 - ${INVERTED_LIFT.toFixed(2)});
  color = mix(clamp(color, 0.0, 1.0), inverse, u_invert);
  gl_FragColor = vec4(color, alphaAlong * alphaAcross);
}
`;

// The gradient stops declared as mk-* tokens in app/assets/tailwind/application.css.
export const RIBBON_TOKENS = [
  "--color-mk-ribbon-green",
  "--color-mk-ribbon-teal",
  "--color-mk-ribbon-blue",
  "--color-mk-ribbon-navy",
] as const;
const FALLBACK_COLORS = ["#2a9d80", "#137d92", "#145fa0", "#0b4f9c"];

const MAX_PIXEL_RATIO = 1.5;
const SCROLL_SMOOTHING = 0.12;
const COLOR_LIFT = 0.2;
const FADE_MS = 600;

// Control points in the orthographic camera's space (x in [-aspect, aspect],
// y in [-1, 1]). The hero band follows the original rarion.uk path (#3199):
// it enters above the top edge right of centre, swings left across the upper
// third, then sweeps back down and out past the right edge, so it crosses the
// end of the headline, which is where the inversion reads. The closing band
// is its own path, the v5 one that rises out of the bottom-left of the pale
// closing panel, frozen here so the hero's return leaves that panel as it was.
const HERO_POINTS: ControlPoints = [
  [0.35, 1.25, 0],
  [-0.05, 0.55, 0.35],
  [0.5, -0.35, -0.35],
  [1.35, -1.2, 0],
];
const CLOSING_POINTS: ControlPoints = [
  [0.44, -1.3, 0],
  [0.3, -0.5, 0.35],
  [-0.3, 0.1, -0.35],
  [-1.35, 1.0, 0],
];

export function controlPoints(placement: Placement, aspect: number): ControlPoints {
  const points = placement === "closing" ? CLOSING_POINTS : HERO_POINTS;
  return points.map(([x, y, z]) => [x * aspect, y, z]) as ControlPoints;
}

// How far the host has scrolled out of the top of the viewport, 0 while its
// top edge is still on screen and 1 once it has gone; drives u_scroll.
export function scrollProgress(top: number, height: number): number {
  if (height <= 0) return 0;
  return Math.min(Math.max(-top / height, 0), 1);
}

export function shouldAnimate(env: { reducedMotion: boolean; webgl: boolean; hosts: number }) {
  return env.hosts > 0 && !env.reducedMotion && env.webgl;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function canUseWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return false;
    // Hand the probe's context back: Safari caps live contexts per page and
    // evicts the oldest once the cap is hit.
    (gl as WebGLRenderingContext).getExtension("WEBGL_lose_context")?.loseContext();
    return true;
  } catch {
    return false;
  }
}

export function hosts(root: ParentNode = document): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>("[data-ribbon]"));
}

export function placementOf(host: HTMLElement): Placement {
  return host.dataset.ribbon === "closing" ? "closing" : "hero";
}

// Whether the stylesheet blends the host itself in difference (the phone
// path on iOS WebKit, see application.css): the band then paints itself
// inverted so the blend gives its true colours back. Re-read on every
// layout, because a rotation can carry the host across the breakpoint.
export function paintsInverted(host: HTMLElement): boolean {
  return getComputedStyle(host).getPropertyValue("mix-blend-mode").trim() === "difference";
}

// Reserves the closing host's phone strip (see _ribbon.html.erb) before the
// shader loads.
export function markReady(host: HTMLElement): void {
  host.dataset.ribbonReady = "";
  // The phone strip takes its height here, which changes the document's
  // height; anything that measured the page re-measures on this.
  host.dispatchEvent(new CustomEvent(RIBBON_READY_EVENT, { bubbles: true }));
}

export function readRibbonColors(root: Element = document.documentElement): string[] {
  const style = getComputedStyle(root);
  return RIBBON_TOKENS.map(
    (token, i) => style.getPropertyValue(token).trim() || FALLBACK_COLORS[i],
  );
}

// DOM helpers, kept free of WebGL so they can be unit-tested in jsdom.
export function attachCanvas(host: HTMLElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.dataset.ribbonCanvas = "";
  canvas.setAttribute("aria-hidden", "true");
  Object.assign(canvas.style, {
    position: "absolute",
    inset: "0",
    width: "100%",
    height: "100%",
    display: "block",
    opacity: "0",
    transition: `opacity ${FADE_MS}ms ease`,
  });
  host.appendChild(canvas);
  return canvas;
}

// Fade the canvas in once a frame has rendered; the host is empty until then.
export function reveal(canvas: HTMLCanvasElement): void {
  canvas.style.opacity = "1";
}

// Back to the empty host, with the closing host's phone strip collapsed again.
export function restore(host: HTMLElement): void {
  for (const canvas of host.querySelectorAll("[data-ribbon-canvas]")) canvas.remove();
  delete host.dataset.ribbonReady;
}

// animated: false renders one still frame (reduced motion) instead of the loop.
export function mountRibbon(
  three: Three,
  host: HTMLElement,
  placement: Placement,
  colors: string[],
  animated = true,
): RibbonHandle {
  const canvas = attachCanvas(host);
  const renderer = new three.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    premultipliedAlpha: false,
    // A decoration: the integrated GPU on a dual-GPU laptop, the battery on a phone.
    powerPreference: "low-power",
  });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO));

  const scene = new three.Scene();
  const camera = new three.OrthographicCamera(-1, 1, 1, -1, -10, 10);
  camera.position.set(0, 0, 5);

  // 220 segments along × 8 across keeps the cross-section arc smooth.
  const geometry = new three.PlaneGeometry(1, 1, 220, 8);
  // The shader's edge and rim shading multiplies these down, so the flat
  // token colours are lifted towards white first to keep the silk luminous.
  const white = new three.Color("#ffffff");
  const [green, teal, blue, navy] = colors.map((c) => new three.Color(c).lerp(white, COLOR_LIFT));
  const uniforms = {
    u_time: { value: 0 },
    u_scroll: { value: 0 },
    u_reverse: { value: placement === "closing" ? 1 : 0 },
    u_invert: { value: 0 },
    u_cp0: { value: new three.Vector3() },
    u_cp1: { value: new three.Vector3() },
    u_cp2: { value: new three.Vector3() },
    u_cp3: { value: new three.Vector3() },
    u_color0: { value: green },
    u_color1: { value: teal },
    u_color2: { value: blue },
    u_color3: { value: navy },
    u_color4: { value: green.clone().lerp(white, 0.3) },
  };
  const material = new three.ShaderMaterial({
    uniforms,
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    transparent: true,
    side: three.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  scene.add(new three.Mesh(geometry, material));

  const layout = () => {
    const width = host.clientWidth;
    const height = host.clientHeight;
    if (width === 0 || height === 0) return;
    uniforms.u_invert.value = paintsInverted(host) ? 1 : 0;
    renderer.setSize(width, height, false);
    const aspect = width / height;
    camera.left = -aspect;
    camera.right = aspect;
    camera.top = 1;
    camera.bottom = -1;
    camera.updateProjectionMatrix();
    const [p0, p1, p2, p3] = controlPoints(placement, aspect);
    uniforms.u_cp0.value.set(...p0);
    uniforms.u_cp1.value.set(...p1);
    uniforms.u_cp2.value.set(...p2);
    uniforms.u_cp3.value.set(...p3);
  };
  // Render only while on screen and the tab is visible; the closing band
  // would otherwise animate unseen for the whole visit. When not animated
  // (reduced motion) each schedule() renders a single still frame.
  let visible = true;
  let revealed = false;
  let lost = false;
  let raf = 0;
  let scroll = 0;
  let pushed: number | null = null;
  const start = performance.now();
  const frame = () => {
    raf = 0;
    if (!visible || lost || document.hidden) return;
    if (animated) {
      uniforms.u_time.value = (performance.now() - start) / 1000;
      let target = pushed;
      if (target === null) {
        const rect = host.getBoundingClientRect();
        target = scrollProgress(rect.top, rect.height);
      }
      scroll += (target - scroll) * SCROLL_SMOOTHING;
      uniforms.u_scroll.value = scroll;
    } else {
      uniforms.u_time.value = 0;
      uniforms.u_scroll.value = 0;
    }
    renderer.render(scene, camera);
    if (!revealed) {
      revealed = true;
      reveal(canvas);
    }
    if (animated) raf = requestAnimationFrame(frame);
  };
  const schedule = () => {
    if (raf === 0 && visible && !lost && !document.hidden) raf = requestAnimationFrame(frame);
  };
  // Phones drop WebGL contexts under memory pressure or when the tab goes to
  // the background, and restore them later. preventDefault asks for that
  // restore; until it comes the loop stands down instead of pushing frames
  // into a dead context, and afterwards the band lays itself out again.
  const onContextLost = (event: Event) => {
    event.preventDefault();
    lost = true;
    cancelAnimationFrame(raf);
    raf = 0;
  };
  const onContextRestored = () => {
    lost = false;
    layout();
    schedule();
  };
  canvas.addEventListener("webglcontextlost", onContextLost);
  canvas.addEventListener("webglcontextrestored", onContextRestored);
  layout();
  const resize = new ResizeObserver(() => {
    layout();
    schedule();
  });
  resize.observe(host);
  const intersection = new IntersectionObserver(
    (entries) => {
      visible = entries.some((entry) => entry.isIntersecting);
      schedule();
    },
    { threshold: 0 },
  );
  intersection.observe(host);
  document.addEventListener("visibilitychange", schedule);
  schedule();

  return {
    placement,
    setScrollProgress(progress: number | null) {
      pushed = progress === null ? null : Math.min(Math.max(progress, 0), 1);
    },
    setAnimated(on: boolean) {
      if (animated === on) return;
      animated = on;
      cancelAnimationFrame(raf);
      raf = 0;
      schedule();
    },
    dispose() {
      visible = false;
      cancelAnimationFrame(raf);
      raf = 0;
      intersection.disconnect();
      resize.disconnect();
      document.removeEventListener("visibilitychange", schedule);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      canvas.removeEventListener("webglcontextrestored", onContextRestored);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      restore(host);
    },
  };
}

function whenIdle(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => resolve(), { timeout: 1500 });
    } else {
      setTimeout(resolve, 200);
    }
  });
}

// Entry point: gate on WebGL, reserve the hosts, wait for idle, lazy-load
// three.js and mount in every [data-ribbon] host. Reduced motion still gets
// the ribbon, as a single still frame; a later change of that preference
// freezes or resumes the loop rather than removing the band.
export async function boot(root: Document = document): Promise<RibbonHandle[]> {
  const targets = hosts(root);
  if (targets.length === 0 || !canUseWebGL()) return [];
  // Before the idle wait, so the phone strips are laid out with the page.
  for (const host of targets) markReady(host);

  await whenIdle();
  let three: Three;
  try {
    three = await import("three");
  } catch {
    for (const host of targets) restore(host);
    return [];
  }

  const colors = readRibbonColors(root.documentElement);
  const animated = !prefersReducedMotion();
  const handles: RibbonHandle[] = [];
  for (const host of targets) {
    try {
      handles.push(mountRibbon(three, host, placementOf(host), colors, animated));
    } catch {
      // No context after all (a blocklisted GPU, Safari's per-page cap): the
      // host goes back to nothing rather than a blank strip.
      restore(host);
    }
  }

  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  query.addEventListener("change", (event: MediaQueryListEvent) => {
    for (const handle of handles) handle.setAnimated(!event.matches);
  });
  return handles;
}
